import { Component, Input, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-attachment-view-modal',
  standalone: true,
  imports: [CommonModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './attachment-view-modal.component.html',
  styleUrl: './attachment-view-modal.component.scss',
})
export class AttachmentViewModalComponent {
  @Input() fileUrl?: string | null;
  @Input() downloadUrl?: string | null;
  @Input() fileName?: string;
  @Input() fileType?: string; // MIME type for direct previews e.g. 'application/pdf'
  @Input() isLoading = false;

  private static readonly IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'tiff', 'tif']);

  public get isImage(): boolean {
    if (this.fileType) return this.fileType.startsWith('image/');
    const ext = (this.fileName ?? '').split('.').pop()?.toLowerCase() ?? '';
    return AttachmentViewModalComponent.IMAGE_EXTENSIONS.has(ext);
  }

  public get isPdf(): boolean {
    if (this.fileType) return this.fileType === 'application/pdf';
    const ext = (this.fileName ?? '').split('.').pop()?.toLowerCase() ?? '';
    return ext === 'pdf';
  }

  private safeUrl: SafeResourceUrl | null = null;
  private safeUrlSource: string | null = null;

  public get safeFileUrl(): SafeResourceUrl | null {
    if (!this.fileUrl) return null;
    if (this.fileUrl !== this.safeUrlSource) {
      this.safeUrlSource = this.fileUrl;
      // #toolbar=0 hides the browser PDF viewer's toolbar; the fragment isn't sent to the
      // server, so it doesn't affect the SAS signature.
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.fileUrl}#toolbar=0&navpanes=0`);
    }
    return this.safeUrl;
  }

  // Zoom & pan state
  public scale: number = 1;
  public translateX: number = 0;
  public translateY: number = 0;
  public isDragging: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private readonly minScale = 1;
  private readonly maxScale = 5;
  private readonly zoomStep = 0.15;

  constructor(private readonly modalRef: NgbActiveModal, private readonly sanitizer: DomSanitizer) {}

  public get imageTransform(): string {
    return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }

  public onWheel(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -this.zoomStep : this.zoomStep;
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, this.scale + delta));
    if (this.scale === this.minScale) {
      this.translateX = 0;
      this.translateY = 0;
    }
  }

  public onMouseDown(event: MouseEvent): void {
    if (this.scale <= 1) return;
    event.preventDefault();
    this.isDragging = true;
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
  }

  @HostListener('document:mousemove', ['$event'])
  public onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const dx = event.clientX - this.lastMouseX;
    const dy = event.clientY - this.lastMouseY;
    this.translateX += dx;
    this.translateY += dy;
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
  }

  @HostListener('document:mouseup')
  public onMouseUp(): void {
    this.isDragging = false;
  }

  public resetZoom(): void {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
  }

  public download(): void {
    const url = this.downloadUrl ?? this.fileUrl;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = this.fileName ?? 'attachment';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  public close(): void {
    this.modalRef.close();
  }
}
